/**
 * The suggestion box, as a pure module.
 *
 * The box itself is a PostHog survey — PostHog renders the form, stores the answers
 * next to E3's, and owns the wording, so changing the question is not a deploy. This
 * app owns exactly one thing: a button carrying the class the survey watches. The two
 * halves meet at `SUGGESTION_TRIGGER_CLASS` and nowhere else.
 *
 * Nothing here talks to PostHog. `hasSuggestionBox` is handed a survey list and answers
 * whether one of them is ours.
 */

/**
 * The class on our button, and the `widgetSelector` on the PostHog survey. Renaming it
 * here without renaming it there leaves a button that opens nothing — which is why the
 * page asks `hasSuggestionBox` before rendering at all, rather than assuming.
 */
export const SUGGESTION_TRIGGER_CLASS = 'suggestion-box-trigger';

/** The only copy this app owns. Everything inside the form comes from PostHog. */
export const SUGGESTION_LABEL = 'Suggest an idea';

/** The slice of a PostHog survey this module reads. */
export interface TriggerSurvey {
  type?: string;
  appearance?: {
    widgetType?: string;
    widgetSelector?: string;
  } | null;
}

/**
 * Is one of these surveys the suggestion box?
 *
 * Matched on the selector rather than on a survey id: an id pins the page to one row in
 * PostHog, and re-creating the survey — which is how a survey gets edited once it has
 * run — would silently take the button away. The selector is the actual contract.
 *
 * A survey that is live but not a selector widget is somebody else's (E3 is a popover),
 * and must not put a button on the page.
 */
export function hasSuggestionBox(surveys: readonly TriggerSurvey[]): boolean {
  return surveys.some(
    (survey) =>
      survey.type === 'widget' &&
      survey.appearance?.widgetType === 'selector' &&
      typeof survey.appearance.widgetSelector === 'string' &&
      survey.appearance.widgetSelector.includes(`.${SUGGESTION_TRIGGER_CLASS}`),
  );
}
